import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import firebase from 'firebase/compat/app';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CustomButton } from '../components/Button';
import { LANG_TEXT } from '../constants/language';
import { db } from '../firebase/config';
import DB_TABLE from '../firebase/db-config';
import { updateOrderDoc } from '../api/order-api';
import { fetchMultipleSeatsByTableNumbers, normalizeObject, omitFields, translate } from '../utils/common';
import { getOverallStatus } from '../utils/multiOrder.utils';
import { BillStatus, DamagedItemStatus, ICreateOrder, MultiOrderSegment, Order, OrderItemStatus, Restaurant } from '../views/Orders/order.types';
import { useAuthContext } from './authContext';
import { useNotificationContext } from './notificationContext';

interface OrderContextValue {
  orderList: Order[];
  newOrders: Order[];
  ongoingOrders: Order[];
  deliveredOrders: Order[];
  preOrders: Order[];
  loading: boolean;
  seatDataMap: Map<string, any>;
  handleUpdateOrderStatus: (
    orderId: string,
    payload: {
      order_status?: string;
      rider_id?: string;
      order_delivered_time?: firebase.firestore.Timestamp;
      food_promotion_order_status?: OrderItemStatus;
      food_order_status?: OrderItemStatus;
      has_damaged_order?: boolean;
      damaged_number?: number;
      is_damaged?: boolean;
      multi_orders?: MultiOrderSegment[];
    },
    allowRegression?: boolean
  ) => any;
  restaurantList: Restaurant[];
  updateOrderStatusDamaged: (orderId: string, status: string, multiOrders?: string[]) => any;
  handleCreateOrder: (orderId: string, orderDetails: ICreateOrder) => any;
  fetchOrderById: (orderId: string) => Promise<Order | null>;
}

const OrderContext = createContext<OrderContextValue>({
  orderList: [],
  deliveredOrders: [],
  newOrders: [],
  ongoingOrders: [],
  preOrders: [],
  loading: false,
  seatDataMap: new Map(),
  restaurantList: [],
  handleUpdateOrderStatus: (orderId, payload, allowRegression) => {},
  updateOrderStatusDamaged: (orderId, status, multiOrders) => {},
  handleCreateOrder: (orderId, orderDetails) => {},
  fetchOrderById: async (orderId) => null,
});

const getDeliveryRequestStartMinutes = (deliveryRequest?: string) => {
  const match = deliveryRequest?.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const getPreOrderSortTime = (order: Order) => {
  const deliveryRequestStart = getDeliveryRequestStartMinutes(order.delivery_request);
  if (deliveryRequestStart !== null) return deliveryRequestStart;
  return order.requested_delivery_time?.seconds ?? Number.MAX_SAFE_INTEGER;
};

const OrderProvider = ({ children }: { children: ReactNode }) => {
  const { user, stadiumId, stadiumData } = useAuthContext();
  const [seatDataMap, setSeatDataMap] = useState<Map<string, any>>(new Map());
  const { isNotificationEnabled, pushNotification } = useNotificationContext();
  const [showAudioModal, setShowAudioModal] = useState(true);
  const handleManualPlay = async () => {
    try {
      const audio = document.getElementById('audio-service-request-sound') as HTMLAudioElement;
      if (audio) {
        await playAudioOnce(audio);
        setShowAudioModal(false);
      }
    } catch (error) {
      console.warn('Manual play failed', error);
    }
  };

  const isEnable = useRef(isNotificationEnabled);

  useEffect(() => {
    isEnable.current = isNotificationEnabled;
  }, [isNotificationEnabled]);

  const today = useRef(dayjs(dayjs().format('YYYY-MM-DD')).toDate());

  const listRef = useRef<{ [id: string]: Order }>({});
  const lastCreateTime = useRef(0);

  const [loading, setLoading] = useState(true);
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [restaurantList, setRestaurantList] = useState<Restaurant[]>([]);
  const unsubscribeRef = useRef<any>(null);
  // Which orders query applies depends on the stadium version, so wait until the stadium document is loaded.
  const ordersQueryMode = !stadiumData ? undefined : stadiumData.cms_settings?.pre_order === true ? 'v2' : 'v1';

  const getAllOrders = (stadiumId: string) => {
    return db
      .collection(DB_TABLE.DB_TABLE_STADIUM_ALL_ORDERS)
      .where('stadium_id', '==', stadiumId)
      .where('bill_status', '==', BillStatus.CLEARED)
      .where('order_status', '!=', DamagedItemStatus.CANCELLED)
      .where('requested_delivery_time', '>=', today.current)
      .orderBy('requested_delivery_time', 'desc');
  };

  // 1.x stadiums ("ENABLE MENY GO V2" off) have no requested_delivery_time on their orders, and a range filter on a
  // missing field leaves the document out, so getAllOrders can never return them. They use the query on
  // order_create_time that the stadium query replaced.
  const getAllOrdersByRestaurants = (restaurantIds: string[]) => {
    return db
      .collection(DB_TABLE.DB_TABLE_STADIUM_ALL_ORDERS)
      .where('restaurant_id', 'in', restaurantIds)
      .where('bill_status', '==', BillStatus.CLEARED)
      .where('order_status', '!=', DamagedItemStatus.CANCELLED)
      .where('order_create_time', '>=', today.current)
      .orderBy('order_create_time', 'desc');
  };

  const getRestaurantByStadium = (stadium_id: string) => {
    return db.collection(DB_TABLE.DB_TABLE_RESTAURANT_CONFIG).where('stadium_id', '==', stadium_id);
  };

  const handleUpdateOrderStatus = (
    orderId: string,
    payload: {
      order_status?: string;
      order_delivered_time?: any;
      rider_id?: string;
      food_promotion_order_status?: OrderItemStatus;
      food_order_status?: OrderItemStatus;
      damaged_number?: number;
      multi_orders?: MultiOrderSegment[];
    },
    allowRegression?: boolean
  ) => {
    return updateOrderDoc(orderId, payload, allowRegression);
  };

  const handleCreateOrder = async (orderId: string, orderDetails: ICreateOrder) => {
    try {
      await db
        .collection(DB_TABLE.DB_TABLE_STADIUM_ALL_ORDERS)
        .doc(orderId)
        .set({
          ...orderDetails,
        });
      return true;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error, { tags: { order_id: orderId } });
      return false;
    }
  };

  const updateOrderStatusDamaged = async (orderId: string, status: string, multiOrders?: string[]) => {
    try {
      const ordersRef = db.collection(DB_TABLE.DB_TABLE_STADIUM_ALL_ORDERS);
      const snapshot = await ordersRef.where('parent_id', '==', orderId).get();

      if (snapshot.empty) {
        return;
      }

      const targets = snapshot.docs.filter((doc) => {
        const data = doc.data() as Order;
        return !multiOrders?.length || multiOrders.find((r_id) => r_id === data.restaurant_id);
      });

      await Promise.all(
        targets.map((doc) => {
          const data = doc.data() as Order;
          const updatedMultiOrders = data.multi_orders?.map((item) =>
            !multiOrders?.length || multiOrders.includes(item.restaurant_id) ? { ...item, status } : item
          );

          return updateOrderDoc(doc.id, { order_status: status, multi_orders: updatedMultiOrders }).catch((error) => {
            console.error('Error updating damaged order status:', doc.id, error);
          });
        })
      );
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const fetchOrderById = async (orderId: string): Promise<Order | null> => {
    try {
      const orderDoc = await db.collection(DB_TABLE.DB_TABLE_STADIUM_ALL_ORDERS).doc(orderId).get({ source: 'server' });

      if (!orderDoc.exists) {
        console.error('Order not found:', orderId);
        return null;
      }

      const order = orderDoc.data() as Order;
      let data = { ...order, id: orderDoc.id } as Order;

      // Add restaurant data
      const restaurant_id = data.multi_orders?.length === 1 ? data.multi_orders[0].restaurant_id : order.restaurant_id;
      const restaurant = restaurantList.find((r) => r.restaurant_id === restaurant_id) || {
        name: '',
        region: '',
        restaurant_id,
      };

      data = { ...data, restaurant: { ...restaurant }, restaurant_id };

      // Add promotion restaurant data if exists
      if (order.promotion_restaurant_id) {
        const restaurantPromotion = restaurantList.find((r) => r.restaurant_id === order.promotion_restaurant_id) || {
          name: '',
          region: '',
          restaurant_id: order.promotion_restaurant_id,
        };
        data = { ...data, restaurant_promotion: { ...restaurantPromotion } };
      }

      return data;
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      return null;
    }
  };

  const logAudioError = async (userUid: string, errorMessage: string) => {
    const docRef = db.collection('stadium_audio_log').doc(userUid);
    const docSnap = await docRef.get();

    const errorData = {
      error: errorMessage,
      create_time: new Date(),
    };

    if (docSnap.exists) {
      await docRef.update({
        data: firebase.firestore.FieldValue.arrayUnion(errorData),
      });
    } else {
      await docRef.set({
        data: [errorData],
      });
    }
  };

  const playAudioOnce = async (audio: HTMLAudioElement) => {
    audio.volume = 0;
    audio.muted = true;
    audio.currentTime = 0;
    try {
      await audio.play();
      audio.pause();
    } catch (err: any) {
      const errorMsg = err?.message || 'Unknown audio play error';
      console.error('Audio play failed:', errorMsg);
      await logAudioError(user?.uid ?? 'default', errorMsg);
      throw err;
    }
  };

  const fetchOrders = useCallback(async () => {
    if (user?.uid && stadiumId && ordersQueryMode) {
      try {
        setLoading(true);
        listRef.current = {};

        const ordersQuery = ordersQueryMode === 'v2' ? getAllOrders(stadiumId) : getAllOrdersByRestaurants(restaurantList.map((restaurant) => restaurant.restaurant_id));

        unsubscribeRef.current = ordersQuery.onSnapshot(async (querySnapshot) => {
          if (querySnapshot.empty) {
            setOrderList([]);
            setLoading(false);
            lastCreateTime.current = 1;
            return;
          }
          if (querySnapshot.size) {
            const docChanges = querySnapshot.docChanges();

            docChanges.forEach((_change) => {
              const order = _change.doc.data() as Order;
              if (order.is_damaged === true) return;

              const orderCreateDate = dayjs(order.order_create_time.seconds * 1000).format('YYYY-MM-DD');
              // 1.x orders have no requested_delivery_time
              const deliveryDate = order.requested_delivery_time ? dayjs(order.requested_delivery_time.seconds * 1000).format('YYYY-MM-DD') : null;
              const today = dayjs().format('YYYY-MM-DD');

              if (orderCreateDate !== today && !order.is_pre_order) return;
              if (deliveryDate !== today && order?.is_pre_order) return;

              let data = { ...order, id: _change.doc.id } as Order;

              const restaurant_id = data.multi_orders?.length === 1 ? data.multi_orders[0].restaurant_id : order.restaurant_id;
              const restaurant = restaurantList.find((r) => r.restaurant_id === restaurant_id) || {
                name: '',
                region: '',
                restaurant_id,
              };

              data = { ...data, restaurant: { ...restaurant }, restaurant_id };

              if (order.promotion_restaurant_id) {
                const restaurantPromotion = restaurantList.find((r) => r.restaurant_id === order.promotion_restaurant_id) || {
                  name: '',
                  region: '',
                  restaurant_id: order.promotion_restaurant_id,
                };
                data = { ...data, restaurant_promotion: { ...restaurantPromotion } };
              }

              if (data.foods.length > 0) {
                const canRiderSeeOrder = (orderData: Order, userId: string) => {
                  if (orderData.multi_orders && orderData.multi_orders.length > 0) {
                    return orderData.multi_orders.some((segment) => !segment.rider_id || segment.rider_id === userId);
                  }
                  return orderData.rider_id === userId || !orderData.rider_id;
                };

                if (_change.type === 'added') {
                  if (canRiderSeeOrder(data, user?.uid)) {
                    listRef.current[_change.doc.id] = data;
                  }

                  if (!isEnable.current || document?.hidden) {
                    if (
                      (data.order_status === OrderItemStatus.PREPARING || data.order_status === OrderItemStatus.DAMAGED_PREPARING) &&
                      lastCreateTime.current &&
                      lastCreateTime.current < data.order_create_time.seconds &&
                      data.bill_status === BillStatus.CLEARED
                    ) {
                      pushNotification({ data: { status: OrderItemStatus.PREPARING, id: data.id } });
                    }
                  }
                } else if (_change.type === 'removed') {
                  delete listRef.current[_change.doc.id];
                } else if (_change.type === 'modified') {
                  const prevData = listRef.current[_change.doc.id];

                  if (data.multi_orders && data.multi_orders.length > 0) {
                    const mySegments = data.multi_orders.filter((segment) => !segment.rider_id || segment.rider_id === user.uid);

                    if (mySegments.length > 0) {
                      const overallStatus = getOverallStatus(mySegments);
                      const isPreparing = overallStatus === OrderItemStatus.PREPARING || overallStatus === OrderItemStatus.DAMAGED_PREPARING;

                      if (isPreparing || mySegments.some((s) => s.rider_id === user.uid)) {
                        listRef.current[_change.doc.id] = data;
                      }
                    } else {
                      delete listRef.current[_change.doc.id];
                    }
                  } else {
                    const isPreparing = data.order_status === OrderItemStatus.PREPARING || data.order_status === OrderItemStatus.DAMAGED_PREPARING;

                    if (isPreparing) {
                      listRef.current[_change.doc.id] = data;
                    } else if (data.rider_id === user.uid) {
                      listRef.current[_change.doc.id] = data;
                    } else if (data.rider_id && data.rider_id !== user.uid) {
                      delete listRef.current[_change.doc.id];
                    }
                  }

                  if (!isEnable.current || document?.hidden) {
                    const isOnlyPrintMessageChanged = (() => {
                      if (!prevData || !data) return false;

                      const fieldsToOmit = ['print_message', 'print_message_manual'];

                      const cleanPrev = normalizeObject(omitFields(prevData, fieldsToOmit));
                      const cleanNow = normalizeObject(omitFields(data, fieldsToOmit));

                      return JSON.stringify(cleanPrev) === JSON.stringify(cleanNow);
                    })();

                    if (isOnlyPrintMessageChanged) return;

                    let isReadyForPickup = false;
                    let hasMySegment = false;

                    if (data.multi_orders && data.multi_orders.length > 0) {
                      const mySegments = data.multi_orders.filter((segment) => segment.rider_id === user.uid);
                      if (mySegments.length > 0) {
                        hasMySegment = true;
                        const overallStatus = getOverallStatus(mySegments);
                        isReadyForPickup = overallStatus === OrderItemStatus.READY_FOR_PICK_UP || overallStatus === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP;
                      }
                    } else {
                      hasMySegment = data.rider_id === user.uid;
                      isReadyForPickup = data.order_status === OrderItemStatus.READY_FOR_PICK_UP || data.order_status === OrderItemStatus.DAMAGED_READY_FOR_PICK_UP;
                    }

                    if (isReadyForPickup && hasMySegment && data.bill_status === BillStatus.CLEARED) {
                      pushNotification({ data: { status: OrderItemStatus.READY_FOR_PICK_UP, id: data.id } });
                    }
                  }
                }
              }
            });

            const sortedOrders = Object.values(listRef.current).sort((a, b) => (a.order_create_time.seconds < b.order_create_time.seconds ? 1 : -1));
            setOrderList(sortedOrders);
            lastCreateTime.current = Math.max(sortedOrders[0]?.order_create_time?.seconds, lastCreateTime.current);

            setLoading(false);
          }
        }, (error) => {
          // e.g. failed-precondition when the query needs a composite index that is not deployed: without this the
          // rider just sees an empty list and nothing is logged.
          console.error('Error listening to orders:', error);
          Sentry.captureException(error, { tags: { listener: 'rider_orders', orders_query: ordersQueryMode } });
          setLoading(false);
        });
        return () => unsubscribeRef.current?.();
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
  }, [user?.uid && restaurantList, ordersQueryMode]);

  const fetchRestaurants = useCallback(async () => {
    if (user?.uid && stadiumId) {
      try {
        setLoading(true);
        const restaurantsRef = await getRestaurantByStadium(stadiumId).get();
        const restaurants = restaurantsRef.docs.map((r) => {
          const rest = r.data() as Restaurant;
          return { ...rest, restaurant_id: r.id };
        });

        setRestaurantList([...restaurants]);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    } else if (user?.uid && stadiumId === '') {
      setLoading(false);
    }
  }, [user?.uid, stadiumId]);

  useEffect(() => {
    if (user?.uid && stadiumId) {
      fetchRestaurants();
    }
  }, [user?.uid, stadiumId]);

  useEffect(() => {
    if (user?.uid && restaurantList?.length > 0 && ordersQueryMode) {
      fetchOrders();
      return () => {
        unsubscribeRef.current?.();
      };
    }
  }, [user?.uid && restaurantList, ordersQueryMode]);

  const splitPromotionFoods = (orders: Order[]): Order[] => {
    const updatedOrders: Order[] = [];

    orders.forEach((order) => {
      if (order.promotion_foods && order.promotion_foods.length > 0) {
        const newOrders: Order[] = order.promotion_foods.map((promotionFood) => ({
          ...order,
          promotion_foods: [],
          foods: [promotionFood],
          type: 'promotion',
          order_status: order.food_promotion_order_status as OrderItemStatus,
        }));

        const updatedOrder = { ...order, promotion_foods: [], type: 'normal', order_status: order.food_order_status as OrderItemStatus };

        updatedOrders.push(updatedOrder, ...newOrders);
      } else {
        updatedOrders.push(order);
      }
    });

    return updatedOrders;
  };

  useEffect(() => {
    const loadSeats = async () => {
      if (stadiumId && orderList.length > 0) {
        const uniqueTableNumbers = Array.from(new Set(orderList.map((order) => order.order_table_number).filter(Boolean)));
        const preOrder = stadiumData?.cms_settings?.pre_order ?? false;
        const seats = await fetchMultipleSeatsByTableNumbers(stadiumId, uniqueTableNumbers, preOrder);
        setSeatDataMap(seats);
      }
    };
    loadSeats();
  }, [orderList, stadiumId, stadiumData]);

  const parsedOrderList = useMemo(() => {
    const allowedStatuses = [OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED, DamagedItemStatus.DAMAGED_PREPARING];

    const filteredOrders = orderList.filter((item) => {
      if (!item?.multi_orders || item.multi_orders.length === 0) {
        return !item?.rider_id;
      }

      const myOrders = item.multi_orders?.filter((order) => order.rider_id === user?.uid);
      const status = myOrders?.length ? getOverallStatus(myOrders || []) : item?.order_status;

      if (item?.is_pre_order && allowedStatuses.includes(status as OrderItemStatus)) {
        return false;
      }

      return item.multi_orders.some((order) => !order.rider_id);
    });

    const newOrders = [...filteredOrders].sort((a, b) => {
      const aCreateTimeSeconds = a.order_create_time.seconds;
      const bCreateTimeSeconds = b.order_create_time.seconds;
      if (aCreateTimeSeconds < bCreateTimeSeconds) return -1;
      if (aCreateTimeSeconds > bCreateTimeSeconds) return 1;
      return 0;
    });
    const processedOrders = splitPromotionFoods(orderList);
    const ongoingOrders = processedOrders.filter((item) => {
      const myOrders = item.multi_orders?.filter((order) => order.rider_id === user?.uid) || [];

      const singleOrderCheck =
        [
          OrderItemStatus.DELIVERING,
          OrderItemStatus.PREPARING,
          OrderItemStatus.READY_FOR_PICK_UP,
          OrderItemStatus.DAMAGED_DELIVERING,
          OrderItemStatus.DAMAGED_PREPARING,
          OrderItemStatus.DAMAGED_READY_FOR_PICK_UP,
        ].includes(item?.order_status) && item?.rider_id === user?.uid;

      const multiOrderStatus = getOverallStatus(myOrders);

      const multiOrderCheck = [
        OrderItemStatus.DELIVERING,
        OrderItemStatus.PREPARING,
        OrderItemStatus.READY_FOR_PICK_UP,
        OrderItemStatus.DAMAGED_DELIVERING,
        OrderItemStatus.DAMAGED_PREPARING,
        OrderItemStatus.DAMAGED_READY_FOR_PICK_UP,
      ].includes(multiOrderStatus as OrderItemStatus);

      return singleOrderCheck || multiOrderCheck;
    });

    const deliveredOrders = orderList.filter((item) => {
      const singleOrderCheck = item?.order_status === OrderItemStatus.DELIVERED && item?.rider_id === user?.uid;
      const myOrders = item.multi_orders?.filter((order) => order.rider_id === user?.uid) || [];
      const multiOrderCheck = myOrders?.length && myOrders.every((order) => order.status === OrderItemStatus.DELIVERED);

      return singleOrderCheck || multiOrderCheck;
    });

    const preOrders = orderList
      .filter((item) => {
        const myOrders = item.multi_orders?.filter((order) => order.rider_id === user?.uid);
        const status = myOrders?.length ? getOverallStatus(myOrders || []) : item?.order_status;

        if (!item?.is_pre_order || !allowedStatuses.includes(status as OrderItemStatus)) {
          return false;
        }

        if (!item.requested_delivery_time) {
          return false;
        }
        return item.multi_orders?.some((order) => !order.rider_id);
      })
      .sort((a, b) => {
        const deliveryDiff = getPreOrderSortTime(a) - getPreOrderSortTime(b);
        if (deliveryDiff !== 0) return deliveryDiff;
        return a.order_create_time.seconds - b.order_create_time.seconds;
      });

    return {
      deliveredOrders,
      newOrders,
      ongoingOrders,
      preOrders,
    };
  }, [orderList, user?.uid]);

  return (
    <OrderContext.Provider
      value={{
        loading,
        orderList,
        deliveredOrders: parsedOrderList.deliveredOrders,
        newOrders: parsedOrderList.newOrders,
        ongoingOrders: parsedOrderList.ongoingOrders,
        preOrders: parsedOrderList.preOrders,
        seatDataMap,
        restaurantList,
        handleUpdateOrderStatus,
        updateOrderStatusDamaged,
        handleCreateOrder,
        fetchOrderById,
      }}
    >
      {children}
      {showAudioModal && (
        <Dialog
          open={showAudioModal}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              setShowAudioModal(false);
            }
          }}
          aria-labelledby="hint-dialog-title"
          aria-describedby="hint-dialog-description"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle fontSize={16} textAlign="center" id="audio-unlock-dialog-title">
            {translate(LANG_TEXT.ALLOW_SOUND_PLAYBACK)}
          </DialogTitle>
          <DialogContent>
            <Typography fontSize={14}>{translate(LANG_TEXT.ALLOW_SOUND_TITLE)}</Typography>
            <Typography fontSize={14}>{translate(LANG_TEXT.ALLOW_SOUND_DESCRIPTION)}</Typography>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CustomButton variant="contained" padding="8px 12px" onClick={handleManualPlay}>
              {translate(LANG_TEXT.PLAY_SOUND)}
            </CustomButton>
          </DialogActions>
        </Dialog>
      )}
    </OrderContext.Provider>
  );
};

const useOrderContext = () => {
  const context = useContext(OrderContext);
  return context;
};

export { OrderProvider, useOrderContext };
